import { API_BASE_URL, http } from '../http'
import type {
  LoginPayload,
  LoginResult,
  RegisterPayload,
  ResetPasswordPayload,
  SocialProvider,
  User,
} from '@/types/user'

/**
 * `POST /auth/login`. Returns the user plus both tokens.
 *
 * Business failures arrive as HTTP 200 with an error code — the HTTP layer turns
 * them into an ApiError, so callers only need try/catch. Two codes matter here:
 * INVALID_CREDENTIALS (2) and EMAIL_NOT_VERIFIED (13).
 */
export function login(payload: LoginPayload) {
  return http
    .post<LoginResult>('/auth/login', payload, { meta: { skipAuthRefresh: true } })
    .then((response) => response.data)
}

/** `POST /auth/register`. No tokens: the email must be verified before login. */
export function register(payload: RegisterPayload) {
  return http
    .post<User & { message: string }>('/auth/register', payload, {
      meta: { skipAuthRefresh: true },
    })
    .then((response) => response.data)
}

/**
 * `POST /auth/social/{provider}`. Not a redirect-based OAuth flow: the SPA gets
 * an access token from the provider's own SDK and posts it here.
 */
export function socialLogin(provider: SocialProvider, token: string) {
  return http
    .post<LoginResult>(`/auth/social/${provider}`, { token }, { meta: { skipAuthRefresh: true } })
    .then((response) => response.data)
}

/**
 * `GET /auth/social/discord/redirect` — full URL for a plain anchor/`location.href`,
 * NOT an axios call: Discord's OAuth2 only supports Authorization Code, so this has
 * to be a real browser redirect (see `DiscordSocialLoginController`), unlike
 * `socialLogin()` above.
 */
export const discordLoginRedirectUrl = `${API_BASE_URL}/auth/social/discord/redirect`

/**
 * `POST /auth/social/discord/exchange` — the second half of the Discord SSO
 * redirect: exchanges the short-lived, single-use ticket the backend put in the
 * post-redirect query string (`?discord_login_ticket=...`) for the real tokens.
 * Never the tokens themselves in the URL.
 */
export function exchangeDiscordLoginTicket(ticket: string) {
  return http
    .post<LoginResult>(
      '/auth/social/discord/exchange',
      { ticket },
      { meta: { skipAuthRefresh: true } },
    )
    .then((response) => response.data)
}

/** `POST /auth/logout`. Revokes every token of the user. */
export function logout() {
  return http.post<string>('/auth/logout').then((response) => response.data)
}

/** `POST /auth/forgot-password`. Sends the reset link pointing at APP_FE_URL. */
export function forgotPassword(email: string) {
  return http
    .post<string>('/auth/forgot-password', { email }, { meta: { skipAuthRefresh: true } })
    .then((response) => response.data)
}

/** `POST /auth/reset-password`. Token and email come from the email link's query string. */
export function resetPassword(payload: ResetPasswordPayload) {
  return http
    .post<string>('/auth/reset-password', payload, { meta: { skipAuthRefresh: true } })
    .then((response) => response.data)
}

/**
 * `GET /auth/email/verify/{id}/{hash}` — public endpoint.
 * `id` and `hash` are read from the query string of the link we received.
 */
export function verifyEmail(id: string, hash: string) {
  return http
    .get<string>(`/auth/email/verify/${encodeURIComponent(id)}/${encodeURIComponent(hash)}`, {
      meta: { skipAuthRefresh: true },
    })
    .then((response) => response.data)
}

/** `POST /auth/email/resend`. Requires an authenticated (unverified) user. */
export function resendVerificationEmail() {
  return http.post<string>('/auth/email/resend').then((response) => response.data)
}
